import React, { useEffect, useMemo, useSyncExternalStore } from 'react';
import DocumentDownloadField from './DocumentDownloadField';
import {
  Select, Stack, Switch, Text, TextInput,
} from '@mantine/core';
import { createUsePuck } from '@puckeditor/core';
import {
  BUTTON_LINK_KINDS,
  DEFAULT_BUTTON_LINK,
  buttonLinkFromLegacy,
  looksLikeUrl,
} from '../../../shared/boxes/button/buttonLink';
import {
  getVariableCatalogSnapshot,
  getVariableCatalogSource,
  loadVariableCatalog,
  subscribeVariableCatalog,
} from '../../utils/variableCatalogRegistry';
import {
  getWebLinksSnapshot,
  loadWebLinks,
  subscribeWebLinks,
} from '../../utils/webLinkRegistry';
import { buildWebLinkOptions, findWebLink } from '../../utils/webLinks';
import { variableSelectOptions } from '../../utils/variableCatalog';
import { buildSiteLinkTargets } from '../../utils/siteLinkTargets';
import {
  getSiteLinkTargetsSnapshot,
  subscribeSiteLinkTargets,
} from '../../utils/siteLinkTargetsRegistry';
import {
  LostTargetAlert, isLost, withChosenOption, withLostOption,
} from './lostTarget';
import { useSelectedItemProps } from './useSelectedItemProps';
import { puckDebouncedTextField } from './puckDebouncedTextField';
import { renderOptionWithHint, renderWebLinkOption } from './selectOptionHint';

const usePuckApi = createUsePuck();

/* Les helpers de cible perdue vivent désormais dans `lostTarget` — partagés
   avec le champ d'adresse du bloc « Contenu externe ». Réexportés ici : ils y
   sont importés depuis longtemps. */
export { flatOptions, isLost, withLostOption } from './lostTarget';

const KIND_LABELS = {
  page: 'Une page du site',
  block: 'Un bloc du site',
  external: 'Une adresse web',
  email: 'Une adresse e-mail',
  phone: 'Un numéro de téléphone',
  variable: 'Une variable',
  bank: 'Un lien de la banque',
  download: 'Un document à télécharger',
};

/**
 * Les variables dont la valeur ressemble à une adresse, groupées comme dans le
 * menu `@`. Aucune variable n'étant typée côté back, la valeur est le seul
 * indice disponible — d'où le filtre.
 *
 * Sur un modèle de site le catalogue n'a que des noms : le filtre n'y trouverait
 * rien, on propose donc **toutes** les variables (`variableSelectOptions` sans
 * `keep`) et la valeur est écrite à la génération du site du document.
 */
export function urlVariableOptions(catalog) {
  return variableSelectOptions(catalog, (variable) => looksLikeUrl(variable.value));
}

/** Libellé de repli d'un bloc sans titre. Seuls ces types vivent à la racine. */
const BLOCK_TYPE_LABELS = { ProposalSection: 'Bloc', TabsBlock: 'Onglets' };
const labelForType = (type) => BLOCK_TYPE_LABELS[type] || 'Bloc';

/* Le champ URL réutilise le champ débouncé du reste de la sidebar : la frappe
   vit en local, le store n'est écrit qu'à la pause. */
const urlField = puckDebouncedTextField('Adresse', {
  placeholder: 'https://exemple.com',
});

/**
 * Sélecteur de cible d'un bouton — remplace le champ URL libre.
 *
 * Les pages et les blocs viennent du registre du site
 * (`siteLinkTargetsRegistry`), complétés par le contenu VIVANT de la page
 * ouverte : les blocs posés depuis le dernier point de sauvegarde n'ont pas
 * encore rejoint `webData`.
 */
export default function ButtonLinkField({ value, onChange, legacyUrl }) {
  const { itemProps } = useSelectedItemProps();
  const source = useSyncExternalStore(subscribeSiteLinkTargets, getSiteLinkTargetsSnapshot);
  const livePageContent = usePuckApi((s) => s.appState.data.content);

  // Un bouton d'avant le chantier n'a pas d'objet `link` : le sélecteur
  // s'ouvre sur l'URL qu'il portait, plutôt que sur un champ vide. Une slide de
  // carrousel passe la sienne (`ctaUrl`), le bloc sélectionné n'étant pas elle.
  const link = value || buttonLinkFromLegacy(
    legacyUrl === undefined ? itemProps?.url : legacyUrl,
  );
  const patch = (partial) => onChange({ ...DEFAULT_BUTTON_LINK, ...link, ...partial });

  /* Le catalogue de variables ne se charge qu'une fois « Une variable » choisi :
     ouvrir la sidebar d'un bouton ne doit déclencher aucun appel (même règle
     que le menu `@` et que la banque d'images). */
  const {
    catalog, status: catalogStatus,
  } = useSyncExternalStore(subscribeVariableCatalog, getVariableCatalogSnapshot);
  const isVariableKind = link.kind === 'variable';
  useEffect(() => { if (isVariableKind) loadVariableCatalog(); }, [isVariableKind]);

  /* Un modèle de site n'a aucun document rattaché : le back sert ses variables
     avec `variable_names_only`, donc sans valeur. Le filtre « ça ressemble à
     une adresse » n'y trouverait jamais rien, on propose donc la liste entière :
     la variable se choisit par son nom, et sa valeur est écrite à la génération
     du site du document (`buildWebDataForProposal`). */
  const isTemplate = getVariableCatalogSource()?.type === 'web_template';

  const variableOptions = useMemo(
    () => {
      if (!isVariableKind) return [];
      return isTemplate ? variableSelectOptions(catalog) : urlVariableOptions(catalog);
    },
    [isVariableKind, isTemplate, catalog],
  );

  /* Une variable choisie mais écartée du filtre — pas encore renseignée, ou sa
     valeur n'est pas une adresse — n'est PAS une variable perdue : elle vit
     très bien dans le CRM. On la réaffiche par son nom, et l'alerte est
     réservée au cas où le catalogue ne la connaît plus. */
  const chosenVariable = link.variableKey ? catalog.byKey[link.variableKey] : null;
  const variableGone = catalogStatus === 'ready' && !!link.variableKey && !chosenVariable;

  /* Banque de liens : même paresse, même raison. Le sélecteur ne déclenche la
     lecture qu'une fois « Un lien de la banque » choisi. */
  const bank = useSyncExternalStore(subscribeWebLinks, getWebLinksSnapshot);
  const isBankKind = link.kind === 'bank';
  useEffect(() => { if (isBankKind) loadWebLinks(); }, [isBankKind]);

  const bankOptions = useMemo(
    () => (isBankKind ? buildWebLinkOptions(bank.groups) : []),
    [isBankKind, bank.groups],
  );

  const groups = useMemo(() => {
    const { webData, activeSlug } = source;
    if (!webData) return [];
    const live = activeSlug && webData.pages?.[activeSlug]
      ? {
        ...webData,
        pages: {
          ...webData.pages,
          [activeSlug]: {
            ...webData.pages[activeSlug],
            puckData: {
              ...webData.pages[activeSlug].puckData,
              content: livePageContent || webData.pages[activeSlug].puckData?.content,
            },
          },
        },
      }
      : webData;
    return buildSiteLinkTargets(live, labelForType);
  }, [source, livePageContent]);

  /* Une page ou un bloc peut être supprimé après coup. Le lien reste alors en
     base, le `Select` n'a plus d'option qui corresponde et se vide : le champ
     donne à croire qu'aucune cible n'a jamais été choisie, et sur le site
     publié le bouton ne fait plus rien du tout. On garde donc la valeur
     visible, marquée comme perdue, et on le dit.

     `groups` vide ne veut pas dire « tout est supprimé » : c'est aussi l'état
     avant que le registre du site soit alimenté. Sans cette garde, chaque lien
     serait signalé cassé au premier rendu de la sidebar. */
  const targetsKnown = groups.length > 0;

  const pageOptions = groups.map((g) => ({ value: g.pageSlug, label: g.pageTitle }));
  /* Le libellé est celui que l'utilisateur a donné — titre du bloc, sinon
     ancre — et il est relu à chaque rendu : renommer l'un ou l'autre se voit
     immédiatement ici. Restent les blocs que personne n'a nommés, numérotés
     pour ne pas s'appeler tous « Bloc ». */
  const blockOptions = useMemo(() => {
    // Les anciens modèles peuvent réutiliser un id sur plusieurs pages.
    // Comme buildBlockPageIndex, garder la première cible dans l'ordre du site.
    const seen = new Set();
    return groups
      .map((g) => ({
        group: g.pageTitle,
        items: g.blocks.filter((b) => {
          if (seen.has(b.blockId)) return false;
          seen.add(b.blockId);
          return true;
        }).map((b) => ({
          value: b.blockId,
          label: b.hasName ? b.label : `${b.label} n°${b.position}`,
          // Sert la recherche, et s'affiche en second quand le libellé vient du
          // titre — auquel cas l'ancre est une information de plus, pas un doublon.
          anchor: b.anchor === b.label ? '' : b.anchor,
        })),
      }))
      .filter((g) => g.items.length);
  }, [groups]);

  return (
    <Stack gap={12}>
      <Select
        size="xs"
        label="Le bouton mène vers"
        wrapperProps={{ 'data-inspector-layout': 'stacked' }}
        data={BUTTON_LINK_KINDS.map((kind) => ({ value: kind, label: KIND_LABELS[kind] }))}
        value={link.kind}
        onChange={(kind) => kind && patch({ kind })}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
      />

      {link.kind === 'download' && <DocumentDownloadField key={itemProps?.id} value={link} onChange={onChange} withFormSettings />}

      {link.kind === 'page' && (
        <>
          <Select
            size="xs"
            label="Page"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder="Choisir une page"
            data={withLostOption(pageOptions, link.pageSlug, targetsKnown, 'Page supprimée')}
            value={link.pageSlug || null}
            onChange={(pageSlug) => patch({ pageSlug: pageSlug || '' })}
            comboboxProps={{ withinPortal: false }}
          />
          <LostTargetAlert
            shown={targetsKnown && isLost(pageOptions, link.pageSlug)}
            what="La page visée n’existe plus"
          />
        </>
      )}

      {link.kind === 'block' && (
        <>
          <Select
            size="xs"
            label="Bloc"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder="Choisir un bloc"
            data={withLostOption(blockOptions, link.blockId, targetsKnown, 'Bloc supprimé')}
            value={link.blockId || null}
            searchable
            filter={filterBlocks}
            renderOption={renderBlockOption}
            nothingFoundMessage="Aucun bloc"
            // L'ancre n'est plus recopiée : le lien vise l'id du bloc, qui ne
            // change jamais. Une ancre recopiée devenait fausse au premier
            // renommage du bloc visé, et le lien mourait en silence.
            onChange={(blockId) => patch({ blockId: blockId || '' })}
            comboboxProps={{ withinPortal: false }}
          />
          <LostTargetAlert
            shown={targetsKnown && isLost(blockOptions, link.blockId)}
            what="Le bloc visé n’existe plus"
          />
          {!isLost(blockOptions, link.blockId) && link.blockId && (
            <Text size="xs" c="dimmed">
              Le lien suit le bloc, même si vous le renommez ou le déplacez.
            </Text>
          )}
        </>
      )}

      {link.kind === 'external' && (
        <>
          {urlField.render({ value: link.url, onChange: (url) => patch({ url }) })}
          <Switch
            size="sm"
            color="editor"
            label="Ouvrir dans un nouvel onglet"
            checked={link.newTab !== false}
            onChange={(e) => patch({ newTab: e.currentTarget.checked })}
          />
        </>
      )}

      {isVariableKind && (
        <>
          <Select
            size="xs"
            label="Variable"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder="Choisir une variable"
            data={withChosenOption(
              variableOptions,
              link.variableKey,
              chosenVariable?.name || link.variableName,
            )}
            value={link.variableKey || null}
            searchable
            nothingFoundMessage={isTemplate ? 'Aucune variable' : 'Aucune variable ne contient une adresse'}
            /* On copie la valeur, comme le nœud variable d'un texte : le site
               publié ne dépend d'aucune résolution à l'affichage, et le bouton
               « Rafraîchir » la remet à jour. */
            onChange={(key) => {
              const variable = key ? catalog.byKey[key] : null;
              patch({
                variableKey: key || '',
                variableName: variable?.name || '',
                variableValue: variable?.value || '',
              });
            }}
            comboboxProps={{ withinPortal: false }}
          />
          {link.variableKey && (link.variableValue || isTemplate) && (
            <Text size="xs" c="dimmed">
              {link.variableValue
                ? `Valeur actuelle : ${link.variableValue}`
                : 'La valeur sera lue dans le document à la génération du site.'}
            </Text>
          )}
          {/* Une valeur manquante se VOIT : le bouton est inerte sur le site
              publié, et une ligne grise de plus passait inaperçue. Un modèle
              n'est pas concerné — ses variables n'ont jamais de valeur. */}
          <LostTargetAlert
            shown={!!link.variableKey && !link.variableValue && !isTemplate && !variableGone}
            what="Cette variable n’est pas encore renseignée dans ce document"
            consequence={'. Le bouton restera inactif tant qu’elle est vide.'}
          />
          {/* `variableOrphan` est posé par le rafraîchissement, qui a vu la clé
              disparaître du CRM ; `variableGone` couvre le cas où le catalogue
              chargé ici ne la connaît plus. Les deux disent la même chose au
              même endroit, mais l'un survit à un rechargement de l'éditeur. */}
          <LostTargetAlert
            shown={link.variableOrphan === true || variableGone}
            what="Cette variable n’existe plus dans le CRM"
          />
          <Switch
            size="sm"
            color="editor"
            label="Ouvrir dans un nouvel onglet"
            checked={link.newTab !== false}
            onChange={(e) => patch({ newTab: e.currentTarget.checked })}
          />
        </>
      )}

      {isBankKind && (
        <>
          <Select
            size="xs"
            label="Lien"
            wrapperProps={{ 'data-inspector-layout': 'stacked' }}
            placeholder={bank.status === 'error' ? 'Banque indisponible' : 'Choisir un lien'}
            data={withLostOption(
              bankOptions,
              link.linkId,
              bank.status === 'ready',
              'Lien supprimé',
            )}
            value={link.linkId ? String(link.linkId) : null}
            searchable
            renderOption={renderWebLinkOption}
            disabled={bank.status === 'loading' || bank.status === 'error'}
            nothingFoundMessage="Aucun lien dans la banque"
            /* On copie l'adresse, comme pour une variable : le site publié ne
               dépend d'aucune résolution, et supprimer le lien de la banque ne
               le casse pas. Le bouton « Rafraîchir » remet l'adresse à jour. */
            onChange={(id) => {
              const chosen = findWebLink(bank.links, id);
              patch({
                linkId: id || '',
                linkLabel: chosen?.label || '',
                url: chosen?.url || '',
              });
            }}
            comboboxProps={{ withinPortal: false }}
          />
          {bank.status === 'error' && (
            <Text size="xs" c="dimmed">
              La banque de liens n’a pas pu être lue. Saisissez une adresse web
              en attendant.
            </Text>
          )}
          {link.linkId && link.url && (
            <Text size="xs" c="dimmed">{link.url}</Text>
          )}
          <LostTargetAlert
            shown={bank.status === 'ready' && isLost(bankOptions, String(link.linkId || ''))}
            what="Ce lien n’est plus dans la banque"
            consequence={'. Le bouton continue de mener à l’adresse enregistrée '
              + '; il ne se mettra plus à jour.'}
          />
          <Switch
            size="sm"
            color="editor"
            label="Ouvrir dans un nouvel onglet"
            checked={link.newTab !== false}
            onChange={(e) => patch({ newTab: e.currentTarget.checked })}
          />
        </>
      )}

      {link.kind === 'email' && (
        <TextInput
          size="xs"
          label="Adresse e-mail"
          placeholder="contact@exemple.com"
          value={link.email || ''}
          onChange={(e) => patch({ email: e.currentTarget.value })}
        />
      )}

      {link.kind === 'phone' && (
        <TextInput
          size="xs"
          label="Numéro"
          placeholder="01 23 45 67 89"
          value={link.phone || ''}
          onChange={(e) => patch({ phone: e.currentTarget.value })}
        />
      )}
    </Stack>
  );
}

/**
 * Recherche sur le libellé, l'ancre et le nom de la page : on tape « tarifs »
 * aussi bien pour trouver le bloc que pour trouver la page qui le contient.
 */
export function filterBlocks({ options, search }) {
  const q = search.trim().toLowerCase();
  if (!q) return options;
  const matches = (item, groupLabel) => (
    `${item.label} ${item.anchor || ''} ${groupLabel || ''}`.toLowerCase().includes(q)
  );
  return options
    .map((entry) => (entry.items
      ? { ...entry, items: entry.items.filter((item) => matches(item, entry.group)) }
      : entry))
    .filter((entry) => (entry.items ? entry.items.length > 0 : matches(entry)));
}

/** L'ancre sous le libellé : c'est elle qui finira dans la barre d'adresse. */
const renderBlockOption = renderOptionWithHint(
  (option) => (option.anchor ? `#${option.anchor}` : null),
);

export function puckButtonLinkField(label = 'Lien du bouton') {
  return {
    type: 'custom',
    label,
    render: (props) => <ButtonLinkField {...props} />,
  };
}
